import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Avatar,
    Drawer,
    IconButton,
    Typography,
} from '@material-ui/core';
import {
    Close,
    ExpandMore,
    SwapHoriz,
} from '@material-ui/icons';
import { useRef, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { TouchBackend } from 'react-dnd-touch-backend';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import SmoothScrollPlugin from '../../../components/DraggableRow/smoothScrollingHelper';
import {
    DaysOfWeekNames,
    PeriodType,
    ShiftDisplayType,
} from '../../../Enums';
import OrgJobPathService from '../../../services/OrgJobPath';
import ShiftTemplateSegmentService from '../../../services/ShiftTemplateSegmentService';
import timeService from '../../../services/TimeService';
import { getShiftDisplayName } from '../../../services/ShiftDisplayService';
import { handleMoveRow, handleToggleSelected } from '../../../store/bidSelection';
import './ShiftLineSelectionMobile.scss';

const plugin = new SmoothScrollPlugin();

/**
 * @param {{
 *  isMultipleSelection: boolean;
 *  daysPerPeriod: number;
 * }} props
 */
function ShiftLineSelectionMobile(props) {
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'employee' });
    const dispatch = useDispatch();

    /** @type {ShiftBidding.EmployeeStore} */
    // @ts-ignore
    const { employeeBidSelection, employeeBidProfile } = useSelector((state) => state);
    const {
        anchorDay, filteredItems, shifts: shiftsDictionary, dayCount, periodType,
    } = employeeBidSelection;
    const { isGroupShiftlinesWithSameSchedules, shiftDisplayType = ShiftDisplayType.ShiftLabel } = employeeBidProfile;
    const { isMultipleSelection = false, daysPerPeriod } = props;

    const [showMoreDetails, setShowMoreDetails] = useState(false);
    const [currentShiftDetailed, setCurrentShiftDetailed] = useState(filteredItems[0]);

    const maxNbWeek = currentShiftDetailed && currentShiftDetailed.weeklyHeight.length;
    const currentShiftName = currentShiftDetailed && (
        currentShiftDetailed.workplanName || currentShiftDetailed.schedulePatternTemplate.name
    );
    const smallCurrentShiftOrgJobPath = currentShiftDetailed && OrgJobPathService.getShortShiftOrgJobPath(currentShiftDetailed.orgJobPath);

    /**
     * @param {{
     * rowData: EmployeeBidProfile.IGroupedScheduleTemplateItems,
     * render: JSX.Element,
     * classes: string,
     * canDrag: boolean,
     * }} param0
     */
    function DraggableRow({
        rowData, render, classes, canDrag,
    }) {
        if (canDrag) {
            const ref = useRef(null);
            const selectedId = isGroupShiftlinesWithSameSchedules
                                            ? rowData.groupId
                                            : rowData.id.toString();
            const [{ canDrop, isOver }, drop] = useDrop({
                accept: 'ROW',
                collect: (/** @type {{ isOver: () => any; canDrop: () => any; }} */ monitor) => ({
                    isOver: monitor.isOver(),
                    canDrop: monitor.canDrop(),
                }),
                /**
                 * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} item
                 * @param {any} monitor
                 */
                drop(item, monitor) {
                    dispatch(handleMoveRow(employeeBidSelection, item, rowData, filteredItems, isGroupShiftlinesWithSameSchedules));
                },
            });

            const [{ isDragging }, drag] = useDrag({
                type: 'ROW',
                item: () => {
                    plugin.onDragStart();
                    return rowData;
                },
                end: () => {
                    plugin.onDragEnd();
                },
                collect: (/** @type {{ isDragging: () => any; }} */ monitor) => ({
                    isDragging: monitor.isDragging(),
                }),
            });
            const isActive = canDrop && isOver;
            /** @type {import('@material-ui/styles').CSSProperties} */
            const dynamicStyles = {
                opacity: isDragging ? 0.5 : 1,
                background: isActive ? '#DDDDDD33' : undefined,
            };
            drag(drop(ref));
            return (
                <div
                    ref={ref}
                    key={`draggableRow_${selectedId}`}
                    className={classes}
                    style={dynamicStyles}
                >
                    {render}
                </div>
            );
        }
        return (
            <div className="shiftContainer MuiPaper-elevation2">
                {render}
            </div>
        );
    }

    /**
     * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} selectedSchedule
     */
    const handleOpenDetails = (selectedSchedule) => {
        setShowMoreDetails(true);
        setCurrentShiftDetailed(selectedSchedule);
    };
    /**
     * @param {string} shiftTemplateId
     * @returns {BidProfile.IShiftTemplate}
     */
    const getShiftTemplate = (shiftTemplateId) => shiftsDictionary[shiftTemplateId];

    /**
     * @param {number} week
     * @param {number} dayIndex
     * @param {number} shiftOrder
     * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} schedule
     */
    const generateDay = (week, dayIndex, shiftOrder, schedule) => {
        const { shifts } = schedule;
        const isPeriodTypeDay = periodType === PeriodType.Days;
        const labelDayIndex = isPeriodTypeDay ? dayIndex : (dayIndex + anchorDay) % daysPerPeriod;

        const currentShift = shifts[week]?.[dayIndex]?.[shiftOrder];
        const shiftTemplate = currentShift && getShiftTemplate(currentShift.shiftTemplate);
        const numberShiftsInDay = currentShift ? shifts[week][dayIndex].length : 0;
        const isDayOutsidePeriod = (week * daysPerPeriod) + (dayIndex + 1) > dayCount;
        const selectedId = isGroupShiftlinesWithSameSchedules
                                    ? schedule.groupId
                                    : schedule.id.toString();
        return (
            <div
                className="dayContainer"
                key={`${selectedId}_${week}_${dayIndex}`}
            >
                {!week && (
                    <div className="dayOfWeekLabel">
                        {isPeriodTypeDay ? labelDayIndex + 1 : DaysOfWeekNames[labelDayIndex]?.substring(0, 2)}
                    </div>
                )}
                {(currentShift && schedule.shifts[week][dayIndex].length)
                    ? (
                        <div className="shiftHoursContainer shiftSquareContainer">
                            {getShiftDisplayName(shiftTemplate, shiftDisplayType)}
                            {numberShiftsInDay > 1 && (
                                <span className="numberShiftsDay">{` +${numberShiftsInDay - 1}`}</span>
                            )}
                            {ShiftTemplateSegmentService.hasTransfer(shiftTemplate.segments) && <SwapHoriz />}
                        </div>
                    ) : (
                        // eslint-disable-next-line max-len
                        <div className={`dayContainer shiftSquareContainer emptyDay shiftHoursLabel ${isDayOutsidePeriod ? 'sst-lightgray' : ''}`}>
                            <div className="blueSquareHeight" />
                        </div>
                    )}
            </div>
        );
    };

    /**
     * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} item
     * @returns {JSX.Element}
     */
    const generateOrder = (item) => {
        /**
         * @type {any}
         */
        let avatarLabel = ''
        const selectedId = isGroupShiftlinesWithSameSchedules
                                            ? item.groupId
                                            : item.id.toString();
        const index = employeeBidSelection.employeeSelections.findIndex((x) => x === selectedId);
        if (index !== -1) {
            avatarLabel = isMultipleSelection ? index + 1 : '✓';
        }
        const avatarStyle = avatarLabel ? 'selected-order' : '';
        return (
            <div className={`ssm-avatar-container-centered ${item.rowClassName}`}>
                <Avatar
                    className={`table-order-avatar ${avatarStyle}`}
                    onClick={() =>
                        dispatch(
                            handleToggleSelected(
                                employeeBidSelection,
                                isMultipleSelection,
                                isGroupShiftlinesWithSameSchedules,
                                item
                            )
                        )}
                >
                    {avatarLabel}
                </Avatar>
            </div>
        );
    };

    /**
     * @param {number} weekIndex
     * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} daySchedule
     */
    const generateWeek = (weekIndex, daySchedule) => {
        const weekShifts = [<></>];
        for (let index = 0; index < daysPerPeriod; index++) {
            weekShifts.push(generateDay(weekIndex, index, 0, daySchedule));
        }
        return (
            <div className="weekContainer">
                <div className="weeklyShiftsContainer">
                    {weekShifts}
                </div>
            </div>
        );
    };

    /**
     * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} schedule
     */
    const dragRowRender = (schedule) => {
        const generatedWeeks = [<></>];
        for (let weeks = 0; weeks < schedule.weeklyHeight.length; weeks++) {
            generatedWeeks.push(generateWeek(weeks, schedule));
        }
        return (
            <div>
                <div className="shiftTitleRowContainer">
                    <div className="shiftTitleRow">
                        <div className="ssm-leftSide">
                            <div className="ssm-firstRow">
                                <div className="ssm-shiftTitleText">
                                    {schedule.schedulePatternTemplate.name}
                                </div>
                                {schedule.quantity > 1 && <Avatar className="srtm-group-quantity-avatar">{schedule.quantity}</Avatar>}
                            </div>
                            {schedule.workplanName && (
                                <span className="ssm-cell-text-content text-md-regular">
                                    {schedule.workplanName}
                                </span>
                            )}
                        </div>
                        <div className="ssm-rightSide">
                            <span className="ssm-cell-text-content text-md-regular">{schedule.orgJobPath}</span>
                            {generateOrder(schedule)}
                        </div>
                    </div>
                </div>
                {generatedWeeks}
                <div
                    className="showMoreText"
                    onClick={() => handleOpenDetails(schedule)}
                >
                    {t('common.viewMoreInfo')}
                </div>
            </div>
        );
    };

    /**
     * @param {number} index
     * @param {number} weekIndex
     */
    const generateDayDetailed = (index, weekIndex) => {
        const isPeriodTypeDay = periodType === PeriodType.Days;
        const labelDayIndex = isPeriodTypeDay ? index : (index + anchorDay) % daysPerPeriod;
        const dayLabel = isPeriodTypeDay ? `Day ${index + 1}` : DaysOfWeekNames[labelDayIndex];
        return (
            <div
                className="detailedShiftContainer"
                key={`${index}_${weekIndex}`}
            >
                {
                    currentShiftDetailed?.shifts[weekIndex] && currentShiftDetailed.shifts[weekIndex][index].length
                        ? (
                            <div className="detailedDayContainer">
                                <div className="detailedDayLabel">{dayLabel}</div>
                                <div className="detailedDayShift">
                                    {
                                        currentShiftDetailed.shifts[weekIndex][index].map((shift) => {
                                            const shiftTemplate = getShiftTemplate(shift.shiftTemplate);
                                            return (
                                                <Accordion
                                                    key={shiftTemplate.id}
                                                    elevation={0}
                                                >
                                                    <AccordionSummary
                                                        expandIcon={<ExpandMore />}
                                                        className="detailedAccordionSummary"
                                                    >
                                                        <div className="singleShiftSign"> </div>
                                                        <div className="detailedShiftHours">
                                                            {
                                                                timeService.calculateShiftTimes(
                                                                    shiftTemplate.startTime,
                                                                    shiftTemplate.endTime,
                                                                )
                                                            }
                                                        </div>
                                                        {ShiftTemplateSegmentService.hasTransfer(shiftTemplate.segments) && <SwapHoriz />}
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        <div className="detailedInfoContainer">
                                                            <div>
                                                                {
                                                                    shiftTemplate.segments.map((x) => {
                                                                        const segmentType = x.segmentTypeId === 1
                                                                            ? ''
                                                                            : x.segmentTypeQualifier.split('_')[0];
                                                                        return (
                                                                            <div key={`segments_${x.id}`}>
                                                                                <div className="detailedSegment">
                                                                                    <div className="detailedSegmentStartTime">
                                                                                        {timeService.calculateTimes(x.startTime)}
                                                                                    </div>
                                                                                    {`[${
                                                                                        // eslint-disable-next-line max-len
                                                                                        timeService.getTimeDifference(x.startTime, x.endTime)
                                                                                    }]`}
                                                                                    <div className="detailedSegmentType">{segmentType}</div>
                                                                                </div>
                                                                                <div className="detailed-job-location">
                                                                                    {x.segmentTypeId === 1
                                                                                        && <div>{smallCurrentShiftOrgJobPath}</div>}
                                                                                    {x.segmentTypeId === 3
                                                                                        && ShiftTemplateSegmentService
                                                                                            .generateShortTransferStrings(
                                                                                                x.transferString || '',
                                                                                                currentShiftDetailed.orgJobPath,
                                                                                            )
                                                                                            .map((s, i) => <div key={`${x.id}-${i}`}>{s}</div>)}
                                                                                    {
                                                                                        x.costCenterDictionary && Object.entries(x.costCenterDictionary).filter(([, value]) => value != null && value !== '').length > 0 && (
                                                                                            <div>
                                                                                                <div>Cost Centers</div>
                                                                                                {
                                                                                                    Object.entries(x.costCenterDictionary).filter(([, value]) => value != null && value !== '').map(([key, value]) => (
                                                                                                    <div key={key}>
                                                                                                        {key}
                                                                                                        : 
                                                                                                        {value}
                                                                                                    </div>
                                                                                                ))
                                                                                                }
                                                                                            </div>
                                                                                        )
                                                                                    }
                                                                                    {
                                                                                        x.segmentSCProfiles && x.segmentSCProfiles.length && (
                                                                                            <div>
                                                                                                <div>Skill and Certification Profiles</div>
                                                                                                {x.segmentSCProfiles.map((x) => (<div key={x.id}>{x.label}</div>))}
                                                                                            </div>
                                                                                        )
                                                                                    }
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })
                                                                }
                                                            </div>
                                                        </div>
                                                    </AccordionDetails>
                                                </Accordion>
                                            );
                                        })
                                    }
                                </div>
                            </div>
                        )
                        : (
                            <div className="detailedEmptyDayContainer">
                                <div className="detailedDayLabel">{dayLabel}</div>
                                <div className="detailedEmptyShift"> </div>
                            </div>
                        )
                }
            </div>
        );
    };

    /**
     * @param {number} weekIndex
     */
    const generateDetailedWeek = (weekIndex) => {
        const weekShifts = [<></>];
        for (let index = 0; index < daysPerPeriod; index++) {
            weekShifts.push(generateDayDetailed(index, weekIndex));
        }
        return (
            <div className="detailedWeekContainer">
                <Typography
                    className="detailedWeekLabel"
                    variant="h6"
                >
                    {`Week ${weekIndex + 1}`}
                </Typography>
                {weekShifts}
            </div>
        );
    };

    const generateDetailedDays = () => {
        const weekShifts = [<></>];
        for (let index = 0; index < dayCount; index++) {
            weekShifts.push(generateDayDetailed(index, Math.floor(index / daysPerPeriod)));
        }
        return (
            <div className="detailedWeekContainer">
                {weekShifts}
            </div>
        );
    };

    return (
        <>
            <DndProvider
                backend={TouchBackend}
                options={{ delayTouchStart: 100 }}
            >
                <div className="shiftsRootContainer">
                    {
                        filteredItems.map((schedule) => {
                            const selectedId = isGroupShiftlinesWithSameSchedules
                                            ? schedule.groupId
                                            : schedule.id.toString();
                            return (
                                <DraggableRow
                                    key={selectedId}
                                    rowData={schedule}
                                    render={dragRowRender(schedule)}
                                    classes="shiftContainer MuiPaper-elevation2"
                                    canDrag={isMultipleSelection}
                                />
                            );
                        })
                    }
                </div>
            </DndProvider>
            <Drawer
                anchor="bottom"
                open={showMoreDetails}
                onClose={() => setShowMoreDetails(false)}
            >
                <div className="detailedDrawerContainer">
                    <div className="detailedDrawerFirstRow">
                        <div className="shiftTitleText">
                            {currentShiftName}
                        </div>
                        <IconButton
                            color="inherit"
                            component="span"
                            onClick={() => setShowMoreDetails(false)}
                        >
                            <Close />
                        </IconButton>
                    </div>
                    <Typography variant="subtitle1">
                        {smallCurrentShiftOrgJobPath}
                    </Typography>
                    <div className="detailedWeeksContainer">
                        {
                            periodType === PeriodType.Days
                                ? generateDetailedDays()
                                : Array.from(Array(maxNbWeek).keys()).map((x) => generateDetailedWeek(x))
                        }
                    </div>
                </div>
            </Drawer>
        </>
    );
}

export default ShiftLineSelectionMobile;
